
import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import { mockRoles } from './mockData';
import type { Role, RoleFormData } from './types';
import RoleModal from './components/RoleModal';
import ConfirmModal from './components/ConfirmModal';

export default function RolesPage() {
  const [roles, setRoles] = useState<Role[]>(mockRoles);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | undefined>();
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const handleCreate = () => {
    try {
      setEditingRole(undefined);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Failed to open create modal:', error);
    }
  };

  const handleEdit = (role: Role) => {
    try {
      setEditingRole(role);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Failed to open edit modal:', error);
    }
  };

  const handleSave = (data: RoleFormData) => {
    try {
      if (editingRole) {
        setRoles((prev) =>
          prev.map((r) =>
            r.id === editingRole.id
              ? { ...r, ...data }
              : r
          )
        );
      } else {
        const maxId = roles.length > 0 ? Math.max(...roles.map((r) => r.id)) : 0;
        const newRole: Role = {
          id: maxId + 1,
          ...data,
          userCount: 0,
          createdAt: new Date().toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          }).replace(/\//g, '-'),
        };
        setRoles((prev) => [newRole, ...prev]);
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Failed to save role:', error);
    }
  };

  const handleDelete = (role: Role) => {
    try {
      setConfirmModal({
        isOpen: true,
        title: '删除角色',
        message: `确定要删除角色"${role.name}"吗？删除后将无法恢复。`,
        onConfirm: () => {
          setRoles((prev) => prev.filter((r) => r.id !== role.id));
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        },
      });
    } catch (error) {
      console.error('Failed to handle delete:', error);
    }
  };

  const handleToggleStatus = (role: Role) => {
    try {
      const newStatus = !role.status;
      setConfirmModal({
        isOpen: true,
        title: newStatus ? '启用角色' : '禁用角色',
        message: `确定要${newStatus ? '启用' : '禁用'}角色"${role.name}"吗？`,
        onConfirm: () => {
          setRoles((prev) =>
            prev.map((r) => (r.id === role.id ? { ...r, status: newStatus } : r))
          );
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        },
      });
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  };

  const getPermissionSummary = (permissions: string[]) => {
    try {
      if (!permissions || !Array.isArray(permissions)) {
        return '0 个模块，0 项权限';
      }
      const modules = new Set<string>();
      permissions.forEach((p) => {
        const module = p.split(':')[0].split('-')[0];
        modules.add(module);
      });
      return `${modules.size} 个模块，${permissions.length} 项权限`;
    } catch (error) {
      console.error('Failed to get permission summary:', error);
      return '权限信息错误';
    }
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">角色管理</h1>
            <p className="text-sm text-gray-500 mt-1">管理系统角色和权限配置</p>
          </div>
          <button
            onClick={handleCreate}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
          >
            <i className="ri-add-line"></i>
            创建角色
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    角色ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    角色名称
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    角色描述
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    权限详情
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    用户数
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    创建时间
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {roles.map((role) => (
                  <tr key={role.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {role.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {role.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                      <div className="line-clamp-2">{role.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {getPermissionSummary(role.permissions)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {role.userCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {role.createdAt}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={role.status}
                          onChange={() => handleToggleStatus(role)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                      </label>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleEdit(role)}
                          className="text-teal-600 hover:text-teal-700 cursor-pointer whitespace-nowrap"
                        >
                          编辑
                        </button>
                        <button
                          onClick={() => handleDelete(role)}
                          className="text-red-600 hover:text-red-700 cursor-pointer whitespace-nowrap"
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <RoleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        role={editingRole}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />
    </MainLayout>
  );
}
